import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, mangaList } from '@/mocks/manga';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find(c => c.slug === slug);
  const categoryManga = currentCategory
    ? mangaList.filter(m => m.category === currentCategory.id)
    : mangaList;

  const displayName = currentCategory?.name || '全部漫画';

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: displayName },
              ]}
            />
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-56 flex-shrink-0">
              <div className="sticky top-20">
                <h3 className="text-sm font-semibold text-foreground-900 mb-3">漫画分类</h3>
                <nav className="space-y-1" aria-label="分类导航">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                        slug === cat.slug
                          ? 'bg-primary-500 text-background-50 font-medium'
                          : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                      }`}
                    >
                      <i className={`${cat.icon} text-base w-5 h-5 flex items-center justify-center`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    {displayName}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">
                    共 <strong className="text-primary-500">{categoryManga.length}</strong> 部作品
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {categoryManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/article/${manga.id}`}
                    className="group block bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-300 transition-all duration-300"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden bg-background-100">
                      <img
                        src={manga.cardImage}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${
                          manga.status === '连载中'
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-foreground-700 text-background-50'
                        }`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-background-950/60 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <i className="ri-star-fill text-accent-500 text-[10px]"></i>
                        <span className="text-background-50 text-[10px] font-medium">{manga.rating}</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-1">
                        {manga.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-600">
                        <span>{manga.author}</span>
                        <span className="text-foreground-300">·</span>
                        <span>共{manga.chapters}话</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {categoryManga.length === 0 && (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-400"></i>
                  <p className="mt-4 text-foreground-600">暂无该分类的漫画</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}